import React from 'react';
import { useSaveStatus } from '../../contexts/SaveStatusContext';
import './SaveStatusIndicator.css';

export type SaveStatus = 'unsaved' | 'tainted' | 'saved' | 'saving';

export interface SaveStatusIndicatorProps {
  className?: string;
}

const SaveStatusIndicator: React.FC<SaveStatusIndicatorProps> = ({ className = '' }) => {
  const { saveStatus, lastSavedTime, currentTime } = useSaveStatus();

  // Format time difference
  const formatTimeAgo = (date: Date): string => {
    const diffInMs = currentTime.getTime() - date.getTime();
    const diffInMins = Math.floor(diffInMs / (1000 * 60));
    
    if (diffInMins < 1) {
      return 'just now';
    } else if (diffInMins === 1) {
      return '1 min ago';
    } else if (diffInMins < 60) {
      return `${diffInMins} mins ago`;
    } else {
      const diffInHours = Math.floor(diffInMins / 60);
      if (diffInHours === 1) {
        return '1 hour ago';
      } else {
        return `${diffInHours} hours ago`;
      }
    }
  };

  const getStatusConfig = () => {
    switch (saveStatus) {
      case 'unsaved':
        return {
          icon: '⚠️',
          text: 'Unsaved',
          description: 'This file has never been saved',
          className: 'status-unsaved'
        };
      case 'tainted':
        return {
          icon: '●',
          text: 'Unsaved changes',
          description: 'You have unsaved changes',
          className: 'status-tainted'
        };
      case 'saving':
        return {
          icon: '⏳',
          text: 'Saving...',
          description: 'Saving your changes',
          className: 'status-saving'
        };
      case 'saved':
        const timeText = lastSavedTime ? `Last saved: ${formatTimeAgo(lastSavedTime)}` : 'Saved';
        return {
          icon: '✓',
          text: timeText,
          description: 'All changes have been saved',
          className: 'status-saved'
        };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <div className={`save-status-indicator ${statusConfig.className} ${className}`}>
      <div className="save-status-content">
        <span className="save-status-icon" title={statusConfig.description}>
          {saveStatus === 'saving' ? (
            <div className="saving-spinner"></div>
          ) : (
            statusConfig.icon
          )}
        </span>
        <span className="save-status-text">
          {statusConfig.text}
        </span>
      </div>
    </div>
  );
};

export default SaveStatusIndicator;
